import styled, { keyframes, css } from "styled-components";

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

export const SignatureCanvasWrapper = styled.div`
  background: white;
  border: 2px dashed
    ${({ theme, $hasSignature }) =>
      $hasSignature ? theme.colors.success : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  ${({ $hasSignature, theme }) =>
    $hasSignature &&
    css`
      border-style: solid;
      border-color: ${theme.colors.success};
      box-shadow: 0 0 0 3px ${theme.colors.successLight};
    `}

  &:hover {
    border-color: ${({ theme, $hasSignature }) =>
      $hasSignature ? theme.colors.success : theme.colors.primary};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    border-style: solid;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.accentLight};
  }
`;

export const SignatureHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const SignatureTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};

  .icon {
    font-size: 1.125rem;
  }
`;

export const SignatureStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  animation: ${fadeIn} 0.3s ease;

  ${({ $signed, theme }) =>
    $signed
      ? css`
          background: ${theme.colors.successLight};
          color: ${theme.colors.success};
        `
      : css`
          background: ${theme.colors.warningLight};
          color: #b06000;
        `}

  .status-icon {
    font-size: 0.875rem;
  }
`;

export const SignaturePad = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 180px;
  background: white;

  canvas {
    touch-action: none;
    cursor: crosshair;
    border-radius: 6px;
  }
`;

export const SignaturePlaceholder = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
  text-align: center;
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const PlaceholderIcon = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.accentLight};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

export const PlaceholderText = styled.div`
  font-size: 0.9375rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const PlaceholderHint = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPlaceholder};
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const SignatureHint = styled.p`
  display: none; /* Hidden - moved info to header */
`;

export const SignatureFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const FooterHint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
  }
`;

export const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.danger};
  border: 1px solid ${({ theme }) => theme.colors.danger};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-family: inherit;
  transition: all 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.dangerLight};
    border-color: ${({ theme }) => theme.colors.danger};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    border-color: ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textMuted};
    border-color: ${({ theme }) => theme.colors.textMuted};
  }
`;
